// A fetch that keeps static downloads in the browser's Cache Storage and
// reports their progress.
//
// The one-time downloads -- the client's wasm binary, its two models and the
// deployment's hints, centroids and indexes -- are ~400 MB at the 1.2B
// deployment. localStorage holds a few MB, so they go in Cache Storage, which
// persists across visits and is sized for this. A cached file is revalidated
// on every use with If-None-Match (or If-Modified-Since): the servers give
// every static /v1 file a content-hash ETag and answer 304 while it is
// unchanged, so a returning visitor downloads only what a rebuild changed,
// and a network failure falls back to the cached copy. A file served as
// immutable -- the deployment's files at their versioned URLs (?v=, see
// docs/api-v1.md) -- is not revalidated at all: a rebuild changes its URL
// instead, and storing the new version drops the old one.
//
// Only GETs that `cacheable` accepts are cached; everything else (the
// per-query POSTs, /v1/info) goes to the network as it is, a GET of it tried
// again if it fails (see download).

export interface Progress {
  url: string;
  loaded: number; // bytes so far
  total: number; // bytes expected (0: unknown)
  cached: boolean; // served from the cache without downloading
  done: boolean;
}

const CACHE_NAME = "websearch-downloads-v1";

// Cache Storage needs a secure context (https, or http://localhost); without
// it every download simply goes to the network. It can also fail in use:
// Safari's private browsing keeps it in memory and refuses large entries, and
// a full disk refuses any. The first failure turns caching off for the rest
// of the page's life -- every later download goes straight to the network --
// rather than failing the search.
let cacheBroken = false;
const brokenListeners: (() => void)[] = [];

function markBroken(err: unknown) {
  if (cacheBroken) return;
  console.warn("Cache Storage failed; downloading without it from now on:", err);
  cacheBroken = true;
  for (const f of brokenListeners) f();
}

// onCacheBroken calls f once Cache Storage turns out not to keep downloads:
// at once if it never could (no secure context), else at its first failure.
// The page says so, since every visit then downloads everything again.
export function onCacheBroken(f: () => void) {
  if (!cacheWorks()) f();
  else brokenListeners.push(f);
}

// Whether downloads are being kept. The worker prefetches only while they
// are: a file fetched but not kept would just be downloaded again.
export const cacheWorks = () => typeof caches !== "undefined" && !cacheBroken;

async function openCache(): Promise<Cache | null> {
  if (!cacheWorks()) return null;
  try {
    return await caches.open(CACHE_NAME);
  } catch (err) {
    markBroken(err);
    return null;
  }
}

// A stored response's size: its Content-Length, which a browser may drop
// from a constructed response, else the copy this file recorded itself.
const SIZE_HEADER = "X-Websearch-Bytes";
const size = (r: Response) =>
  Number(r.headers.get("Content-Length")) || Number(r.headers.get(SIZE_HEADER)) || 0;

// ── Downloads that survive a dropped connection ──
//
// A first visit downloads ~100 files and ~300 MB, and the load needs every
// one, so one dropped connection must not fail it. An attempt that fails --
// the request erroring, a 5xx (a CDN's 52x among them), 408 or 429, or the
// body breaking off or ending short of its Content-Length -- is made again
// after a pause, DOWNLOAD_ATTEMPTS times in all. A body that broke off
// resumes where it stopped, with a Range request for the rest, when its
// response allows: a strong ETag (sent as If-Range, so a file that changed
// meanwhile comes back whole rather than spliced) and no Content-Encoding (a
// range would count the encoded bytes). Anything but the rest of the same
// file starts the download over.
//
// The range is always everything from where the connection broke to the
// end: it says how far the download got, which depends only on the network,
// and never which part of a file a query reads (see readHint in
// search_worker.ts), so it reveals nothing the whole download does not.
export const DOWNLOAD_ATTEMPTS = 5;
// The pause before each further attempt (exported for the tests to shorten).
export const RETRY_DELAYS_MS = [500, 2000, 5000, 10000];

const retryable = (status: number) => status >= 500 || status === 408 || status === 429;

// Waits ms, or until signal aborts (then throws its reason).
function pause(ms: number, signal?: AbortSignal | null): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(signal.reason);
    const t = setTimeout(() => {
      signal?.removeEventListener("abort", stop);
      resolve();
    }, ms);
    const stop = () => {
      clearTimeout(t);
      reject(signal!.reason);
    };
    signal?.addEventListener("abort", stop, { once: true });
  });
}

const fileName = (url: string) => new URL(url).pathname.replace(/^\/v1\//, "").replace(/\.bin$/, "");
const message = (err: unknown) => (err instanceof Error ? err.message : String(err));

function gaveUp(url: string, err: unknown, attempts: number): Error {
  return new Error(`${fileName(url)}: ${message(err)} (gave up after ${attempts} attempts)`);
}

// Where a 206's body starts in the file, or -1.
function rangeStart(resp: Response): number {
  const m = /^bytes (\d+)-/.exec(resp.headers.get("Content-Range") ?? "");
  return m ? Number(m[1]) : -1;
}

export interface Downloaded {
  // The server's answer: the 200 whose body was read (into body), or, unread,
  // any answer not to be tried again -- a 304, a 404 -- or the last retryable
  // one once the attempts ran out.
  resp: Response;
  body?: Blob;
}

// download GETs url with headers (conditional ones included), retrying and
// resuming as above; onLoaded follows the body's bytes.
export async function download(
  base: typeof fetch,
  url: string,
  headers: Headers,
  signal: AbortSignal | null | undefined,
  onLoaded: (loaded: number, total: number) => void,
): Promise<Downloaded> {
  let whole: Response | undefined; // the 200 whose body is being read
  let chunks: Uint8Array[] = [];
  let loaded = 0, total = 0;
  // The body's length to check against: its Content-Length, unless it is
  // encoded, when that counts the compressed bytes and fetch hands over the
  // decoded ones; then 0 (unchecked).
  let expect = 0;
  let etag = "", resumable = false;
  let lastErr: unknown;
  const restart = () => {
    whole = undefined;
    chunks = [];
    loaded = 0;
  };
  for (let attempt = 1; ; attempt++) {
    if (attempt > 1) {
      if (attempt > DOWNLOAD_ATTEMPTS) throw gaveUp(url, lastErr, DOWNLOAD_ATTEMPTS);
      await pause(RETRY_DELAYS_MS[Math.min(attempt - 2, RETRY_DELAYS_MS.length - 1)], signal);
      console.warn(`${fileName(url)}: ${message(lastErr)}; attempt ${attempt} of ${DOWNLOAD_ATTEMPTS}`);
    }
    const h = new Headers(headers);
    const resuming = whole !== undefined && resumable && loaded > 0;
    if (whole) {
      // Mid-download, a 304 would say nothing about the bytes in hand.
      h.delete("If-None-Match");
      h.delete("If-Modified-Since");
      if (resuming) {
        h.set("Range", `bytes=${loaded}-`);
        h.set("If-Range", etag);
      } else restart();
    }
    let resp: Response;
    try {
      resp = await base(url, { headers: h, signal, cache: "no-store" });
    } catch (err) {
      if (signal?.aborted) throw err;
      lastErr = err;
      continue;
    }
    if (retryable(resp.status) && attempt < DOWNLOAD_ATTEMPTS) {
      lastErr = new Error(`${resp.status} ${resp.statusText}`.trim());
      resp.body?.cancel().catch(() => {});
      continue;
    }
    if (resuming && resp.status === 206 && rangeStart(resp) === loaded) {
      // The rest of the same file: read on.
    } else if (resp.status === 200 && resp.body) {
      // The whole file: the first answer, or the server's to a resume it
      // could not serve as a range (the file changed, or no range support).
      restart();
      whole = resp;
      total = size(resp);
      const encoded = !!resp.headers.get("Content-Encoding");
      expect = encoded ? 0 : total;
      etag = resp.headers.get("ETag") ?? "";
      resumable = etag !== "" && !etag.startsWith("W/") && !encoded;
    } else if (whole) {
      // A resume answered some other way (a 416, a 206 for other bytes).
      lastErr = new Error(`resuming at byte ${loaded} got ${resp.status}`);
      resp.body?.cancel().catch(() => {});
      restart();
      continue;
    } else {
      return { resp };
    }
    onLoaded(loaded, total);
    try {
      const reader = resp.body!.getReader();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        loaded += value.byteLength;
        onLoaded(loaded, total);
      }
    } catch (err) {
      if (signal?.aborted) throw err;
      lastErr = err;
      continue;
    }
    if (expect && loaded !== expect) {
      // Ended cleanly but short (or long): never a copy to keep.
      lastErr = new Error(`the download ended at ${loaded} of ${expect} bytes`);
      if (loaded > expect) restart();
      continue;
    }
    const body = new Blob(chunks);
    chunks = [];
    return { resp: whole!, body };
  }
}

// A GET that is not cached (/v1/info) tried again as a download is, but its
// body left unread for the caller; any other request sent once.
async function sendRetrying(base: typeof fetch, input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const req = new Request(input, init);
  if (req.method !== "GET") return base(input, init);
  let lastErr: unknown;
  for (let attempt = 1; ; attempt++) {
    if (attempt > 1) {
      if (attempt > DOWNLOAD_ATTEMPTS) throw gaveUp(req.url, lastErr, DOWNLOAD_ATTEMPTS);
      await pause(RETRY_DELAYS_MS[Math.min(attempt - 2, RETRY_DELAYS_MS.length - 1)], req.signal);
    }
    try {
      const resp = await base(input, init);
      if (!retryable(resp.status) || attempt === DOWNLOAD_ATTEMPTS) return resp;
      lastErr = new Error(`${resp.status} ${resp.statusText}`.trim());
      resp.body?.cancel().catch(() => {});
    } catch (err) {
      if (req.signal?.aborted) throw err;
      lastErr = err;
    }
  }
}

// Whether a stored copy can never go stale.
const immutable = (r: Response) => /\bimmutable\b/.test(r.headers.get("Cache-Control") ?? "");

// Which file a URL names, whatever its version: a versioned URL (?v=) also
// carries a .bin a CDN caches by (api.VersionedPath), which the bare URL
// of the same file lacks.
const fileKey = (url: string) => {
  const u = new URL(url);
  return u.origin + u.pathname.replace(/\.bin$/, "");
};

// The stored copies of url's file under other URLs: other versions, or the
// bare URL an older page stored it under.
async function otherVersions(cache: Cache, url: string): Promise<Request[]> {
  if (!new URL(url).searchParams.has("v")) return [];
  const key = fileKey(url);
  return (await cache.keys()).filter((req) => req.url !== url && fileKey(req.url) === key);
}

// Drops the stored copies of url's file under other URLs, which its newer
// copy replaces; a rebuild would otherwise leave hundreds of MB behind in
// the cache. Best-effort: a stale copy left over is only space.
async function dropOtherVersions(cache: Cache, url: string) {
  try {
    for (const req of await otherVersions(cache, url)) await cache.delete(req);
  } catch (err) {
    console.warn("Cache Storage: dropping old versions failed:", err);
  }
}

// cachedFetch returns the fetch itself and prefetch(url), which brings one
// file's cached copy up to date -- revalidating it, or downloading it -- for a
// later fetch of the same URL to read. A prefetch of a file that is already
// current does not read the copy: the later fetch is what needs its bytes, and
// reading 500 MB twice on every visit would only cost time.
export function cachedFetch(
  base: typeof fetch,
  cacheable: (url: URL) => boolean,
  onProgress: (p: Progress) => void,
): { fetch: typeof fetch; prefetch: (url: string) => Promise<void> } {
  // URLs this fetch has already downloaded or revalidated: the worker
  // prefetches every file and the client then reads it, and one round trip
  // per file per page load is enough.
  const fresh = new Set<string>();

  // The stored copy, read whole here, so a store that fails on reading turns
  // into a network download instead of a broken response; null on failure.
  const fromCache = async (url: string, hit: Response): Promise<Response | null> => {
    try {
      const body = await hit.blob();
      onProgress({ url, loaded: body.size, total: body.size, cached: true, done: true });
      return new Response(body, { status: 200, headers: hit.headers });
    } catch (err) {
      markBroken(err);
      return null;
    }
  };

  // The copy a prefetch found current, reported as read from the cache
  // without reading it.
  const skipRead = (url: string, hit: Response) => {
    const n = size(hit);
    onProgress({ url, loaded: n, total: n, cached: true, done: true });
  };

  // A copy stored under another version of url's file -- an older build's,
  // or the bare URL an older page stored -- stored as url's copy instead,
  // once the server has answered 304 to its ETag: same bytes, new version.
  // A rebuild that leaves a file alone thus costs a round trip, not a
  // download. undefined when the move fails.
  const adopt = async (cache: Cache, url: string, old: Response, resp: Response): Promise<Response | undefined> => {
    try {
      const headers = new Headers(old.headers);
      const cc = resp.headers.get("Cache-Control");
      if (cc) headers.set("Cache-Control", cc);
      const copy = new Response(await old.blob(), { status: 200, headers });
      await cache.put(url, copy.clone());
      await dropOtherVersions(cache, url);
      return copy;
    } catch (err) {
      markBroken(err);
      return undefined;
    }
  };

  const get = async (input: RequestInfo | URL, init: RequestInit | undefined, read: boolean): Promise<Response | null> => {
    const req = new Request(input, init);
    if (req.method !== "GET" || !cacheable(new URL(req.url))) {
      return sendRetrying(base, input, init);
    }
    const url = req.url;
    const cache = await openCache();
    let hit: Response | undefined;
    if (cache) {
      try {
        hit = await cache.match(url);
      } catch (err) {
        markBroken(err);
      }
    }
    if (hit && (fresh.has(url) || immutable(hit))) {
      if (!read) {
        skipRead(url, hit);
        return null;
      }
      const r = await fromCache(url, hit);
      if (r) return r;
      hit = undefined;
    }
    let adopted = false;
    if (!hit && cache) {
      try {
        const [older] = await otherVersions(cache, url);
        hit = older && (await cache.match(older));
        adopted = hit !== undefined;
      } catch (err) {
        markBroken(err);
      }
    }

    const headers = new Headers(req.headers);
    const etag = hit?.headers.get("ETag");
    const modified = hit?.headers.get("Last-Modified");
    if (etag) headers.set("If-None-Match", etag);
    else if (modified) headers.set("If-Modified-Since", modified);

    // A download's progress, at most every 100 ms.
    let lastReport = 0;
    const loading = (loaded: number, total: number) => {
      const now = performance.now();
      if (loaded === 0 || now - lastReport > 100) {
        lastReport = now;
        onProgress({ url, loaded, total, cached: false, done: false });
      }
    };
    const fetchWhole = () => download(base, url, new Headers(), req.signal, loading);

    // download asks with no-store: this cache is the copy; the HTTP cache
    // would be a second one.
    let got: Downloaded;
    try {
      got = await download(base, url, headers, req.signal, loading);
    } catch (err) {
      const r = hit && (await fromCache(url, hit));
      if (r) return r;
      throw err;
    }
    if (got.resp.status === 304 && hit && adopted) {
      hit = await adopt(cache!, url, hit, got.resp);
      if (!hit) got = await fetchWhole();
    }
    if (got.resp.status === 304 && hit) {
      if (!read) {
        // Unread, so a copy that turns out unreadable is found by the fetch
        // that reads it, which then downloads the file (see above).
        fresh.add(url);
        skipRead(url, hit);
        return null;
      }
      const r = await fromCache(url, hit);
      if (r) {
        fresh.add(url);
        return r;
      }
      // The copy is unreadable after all: fetch the file unconditionally.
      got = await fetchWhole();
    }
    const { resp, body } = got;
    if (!body) return resp;

    const loaded = body.size;
    const kept = new Headers();
    for (const h of ["Content-Type", "ETag", "Last-Modified", "Cache-Control"]) {
      const v = resp.headers.get(h);
      if (v) kept.set(h, v);
    }
    kept.set("Content-Length", String(loaded));
    kept.set(SIZE_HEADER, String(loaded));
    const out = new Response(body, { status: 200, headers: kept });
    const keep = await openCache();
    if (keep) {
      try {
        await keep.put(url, out.clone());
        fresh.add(url);
        await dropOtherVersions(keep, url);
      } catch (err) {
        markBroken(err);
      }
    }
    onProgress({ url, loaded, total: loaded, cached: false, done: true });
    return out;
  };

  return {
    fetch: async (input, init) => (await get(input, init, true))!,
    prefetch: async (url) => {
      await get(url, undefined, false);
    },
  };
}

// cachedCopy is url's copy in Cache Storage as a Blob, or null when there is
// none (or no Cache Storage). The Blob is backed by the stored file, not
// read into memory: slicing it reads just the slice, which is how the client
// reads a hint a pack at a time without holding it (search_worker.ts).
export async function cachedCopy(url: string): Promise<Blob | null> {
  const cache = await openCache();
  if (!cache) return null;
  try {
    const hit = await cache.match(url);
    return hit ? await hit.blob() : null;
  } catch (err) {
    markBroken(err);
    return null;
  }
}

// What the cache holds, for the page to show and clear.
export async function cachedBytes(): Promise<number> {
  const cache = await openCache();
  if (!cache) return 0;
  let n = 0;
  for (const req of await cache.keys()) {
    const r = await cache.match(req);
    n += r ? Number(r.headers.get("Content-Length")) || 0 : 0;
  }
  return n;
}

export async function clearCache(): Promise<void> {
  if (typeof caches !== "undefined") await caches.delete(CACHE_NAME);
}
